import React from "react";
import { Box, Grid, SelectChangeEvent, Typography } from "@mui/material";
import { useFormik } from "formik";

import { useDispatchWrapper, usePoliciesByCustomer } from "../../hooks";
import { LoadState, MODULE_IDS } from "../../constants/enums";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../components/formsComponents";
import { StandadCard } from "../../components/Cards";
import { CustomDatePicker } from "../../components/formsComponents/CustomDatePicker";
import { FileUpload } from "../../features/FileUpload/components/FileUpload";
import { RoleBasedCustomButton } from "../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import {
  clearCsioEdocsState,
  fetchCsioEdocsAsync,
  upsertCsioEdocsAsync,
} from "../../redux/csioEdocs/csioEdocsActions";
import { CustomerAutoSearch } from "../../components/AutoCompleteSearches/CustomerAutoSearch";
import { ICustomerFullDetails } from "../../redux/customer/customer.types";
import {
  Business_Purpose_type,
  Business_Types,
  LINE_OF_BUSINESS,
} from "../../constants/constants";
import { useSelector } from "react-redux";
import { IStoreState } from "../../redux/initialStoreState";
import { PageContainer } from "../../components/container/PageContainer";
import { PageLoader } from "../../components/PageLoader/PageLoader";
import { IBreadcrumbProps } from "../../layout/fullLayout/breadcrumb/IBreadcrumbProps";
import { Breadcrumb } from "../../layout/fullLayout/breadcrumb/Breadcrumb";
import { InsurerCodeAutoSearch } from "../../components/AutoCompleteSearches";

export interface ManageCSIOEdocsProps {
  csioEdocsId?: string;
  isUpdate: boolean;
  onSuccess: () => void;
  validation: (values: any) => any;
  breadcrumbItems: IBreadcrumbProps["items"];
  customerId?: string;
  customerPolicyId?: string;
  type?: "guid" | "code";
}

export const ManageCSIOEdocs: React.FC<ManageCSIOEdocsProps> = ({
  csioEdocsId,
  isUpdate,
  onSuccess,
  validation,
  breadcrumbItems,
  customerId,
  customerPolicyId,
  type = "guid",
}) => {
  const { data, dataLoading, error } = useSelector(
    (storeState: IStoreState) => storeState.csioEdocs
  );

  const { policiesList, policiesLoading, fetchPoliciesByCustomerId } =
    usePoliciesByCustomer();
  const [file, setFile] = React.useState<File | null>(null);
  const [saveLoading, setSaveLoading] = React.useState(false);
  const dispatch = useDispatchWrapper();

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setValues,
    setFieldValue,
  } = useFormik({
    initialValues: {
      ...data,
      customer_id: customerId as any,
      customer_policy_id: customerPolicyId as any,
    },
    validate: validation,
    onSubmit: (values) => {
      setSaveLoading(true);
      // console.log("values", values);
      dispatch(
        upsertCsioEdocsAsync({ ...values }, file, (isSuccess) => {
          if (isSuccess) {
            onSuccess();
          }
          setSaveLoading(false);
        })
      );
    },
  });

  const handleCustomer = (data: ICustomerFullDetails) => {
    if (data.customer_id) {
      setValues({
        ...values,
        customer_id: data.customer_id as number,
        customer_name: data.customer_name,
        customer_policy_id: null,
        policy_number: null,
      });
    }
  };

  const handlePolicyChange = (e: SelectChangeEvent<unknown>) => {
    const policyId = e.target.value as string;
    const policy = policiesList.find(
      (x) => x.customer_policy_id === Number(policyId)
    );
    if (policy) {
      setValues({
        ...values,
        customer_policy_id: policyId,
        policy_number: policy.policy_number,
        contract_term_effective_date: policy.effective_date,
        contract_term_expiry_date: policy.expiry_date,
        insurance_company: policy.insurer,
        line_of_business: policy.line_of_business,
        branch_code: policy.branch_code,
        producer_one_id: policy.producer_one,
        producer_code: policy.producer_one_code,
        customer_id: policy.customer_id,
      });
    }
  };

  React.useEffect(() => {
    if (values.customer_id) {
      fetchPoliciesByCustomerId(values.customer_id as number);
    }
  }, [values.customer_id]);

  React.useEffect(() => {
    if (csioEdocsId) {
      dispatch(fetchCsioEdocsAsync(csioEdocsId, type));
    }
  }, [csioEdocsId, dispatch]);

  React.useEffect(() => {
    return () => {
      dispatch(clearCsioEdocsState());
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (customerPolicyId && policiesList.length > 0 && !csioEdocsId) {
      const policy = policiesList.find(
        (x) => x.customer_policy_id === Number(customerPolicyId)
      );
      if (policy) {
        setValues((prevValues) => ({
          ...prevValues,
          customer_policy_id: customerPolicyId,
          policy_number: policy.policy_number,
          contract_term_effective_date: policy.effective_date,
          contract_term_expiry_date: policy.expiry_date,
          insurance_company: policy.insurer,
          line_of_business: policy.line_of_business,
          branch_code: policy.branch_code,
          producer_one_id: policy.producer_one,
          producer_one_code: policy.producer_one_code,
          customer_id: policy.customer_id,
          customer_name: policy.customer_name,
        }));
      }
    } else {
      if (isUpdate) {
        setValues(data);
      }
    }
  }, [policiesList, data, csioEdocsId]);

  const finalPageTitle = csioEdocsId
    ? "Update CSIO EDOCS"
    : "Create CSIO EDOCS";

  return (
    <PageContainer title={finalPageTitle} description="this is innerpage">
      <PageLoader
        loading={dataLoading === LoadState.InProgress}
        error={error ? { message: error } : null}
      >
        {!customerId && !customerPolicyId && (
          <Breadcrumb title="" items={breadcrumbItems} />
        )}
        <StandadCard
          sx={{ mt: 2 }}
          heading={`${isUpdate ? "Update" : "Create"} CSIO EDOCS`}
        >
          <form onSubmit={handleSubmit}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={3}>
                <CustomerAutoSearch
                  label="Customer"
                  value={values.customer_id}
                  onSelect={handleCustomer}
                  error={!!errors.customer_id}
                  // helperText={
                  //   errors.customer_id as string ||
                  //   (customerId && customerPolicyId ? "Auto-filled from policy context" : "")
                  // }
                  disabled={!!(customerId && customerPolicyId)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Policy</CustomFormLabel>
                <ControlledCustomSelect
                  variant="outlined"
                  size="small"
                  type="string"
                  fullWidth
                  displayEmpty
                  name="customer_policy_id"
                  value={Number(values.customer_policy_id)}
                  onChange={handlePolicyChange}
                  placeholder="Select One"
                  options={policiesList.map((item) => {
                    return {
                      label: item.policy_number,
                      value: item.customer_policy_id,
                    };
                  })}
                  // helperText={
                  //   errors.customer_policy_id as string ||
                  //   (customerId && customerPolicyId ? "Auto-filled from policy context" : "")
                  // }
                  disabled={!!(customerId && customerPolicyId)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Type</CustomFormLabel>
                <ControlledCustomSelect
                  name="type"
                  value={values.type}
                  onChange={handleChange}
                  displayEmpty
                  options={Business_Types.map((type) => ({
                    label: type,
                    value: type,
                  }))}
                  fullWidth
                  helperText={errors.type as string}
                />
              </Grid>
              <Grid item xs={12} lg={3}>
                <CustomFormLabel>Financed</CustomFormLabel>
                <ControlledCustomSelect
                  name="financed"
                  fullWidth
                  value={values.financed}
                  onChange={handleChange}
                  variant="outlined"
                  options={[
                    { label: "Yes", value: "Y" },
                    { label: "No", value: "N" },
                  ]}
                  displayEmpty
                  placeholder="Select One"
                />
              </Grid>
              <Grid item xs={12} lg={3}>
                <CustomFormLabel>Financed By</CustomFormLabel>
                <CustomTextField
                  name="financed_by"
                  fullWidth
                  value={values.financed_by}
                  onChange={handleChange}
                  type="text"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomFormLabel>Business Purpose Type</CustomFormLabel>
                <ControlledCustomSelect
                  name="business_purpose_type"
                  value={values.business_purpose_type}
                  onChange={handleChange}
                  displayEmpty
                  options={
                    values.type && Business_Purpose_type[values.type]
                      ? Business_Purpose_type[values.type]
                      : []
                  }
                  fullWidth
                  helperText={errors.business_purpose_type as string}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Insurer</CustomFormLabel>
                <InsurerCodeAutoSearch
                  value={values.insurance_company}
                  fullWidth
                  onSelect={(newValue) => {
                    setValues({
                      ...values,
                      insurance_company: newValue.value,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Effective Date</CustomFormLabel>
                <CustomDatePicker
                  value={values.contract_term_effective_date || ""}
                  onChange={(value) =>
                    setFieldValue("contract_term_effective_date", value)
                  }
                  fullWidth
                  errorMessage={errors.contract_term_effective_date as string}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomFormLabel>Expiry Date</CustomFormLabel>
                <CustomDatePicker
                  value={values.contract_term_expiry_date || ""}
                  onChange={(value) =>
                    setFieldValue("contract_term_expiry_date", value)
                  }
                  fullWidth
                  errorMessage={errors.contract_term_expiry_date as string}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomFormLabel>Line of Business</CustomFormLabel>
                <ControlledCustomSelect
                  variant="outlined"
                  size="small"
                  type="string"
                  name="line_of_business"
                  fullWidth
                  value={values.line_of_business}
                  onChange={handleChange}
                  displayEmpty
                  placeholder={"Select one"}
                  options={LINE_OF_BUSINESS}
                  helperText={errors.line_of_business as string}
                ></ControlledCustomSelect>
              </Grid>
              <Grid item xs={12} lg={3}>
                <CustomFormLabel>Action Needed</CustomFormLabel>
                <ControlledCustomSelect
                  name="action_needed"
                  fullWidth
                  value={values.action_needed}
                  onChange={handleChange}
                  variant="outlined"
                  options={[
                    { label: "Invoice", value: "Invoice" },
                    {
                      label: "Invoice & Email Docs-Customer",
                      value: "Invoice & Email Docs-Customer",
                    },
                    {
                      label: "Invoice & Email Docs-Producer",
                      value: "Invoice & Email Docs-Producer",
                    },
                    {
                      label: "Invoice & Email Docs - Customer & Producer",
                      value: "Invoice & Email Docs - Customer & Producer",
                    },
                  ]}
                  displayEmpty
                  placeholder="Select One"
                />
              </Grid>
              <Grid item xs={12} lg={3}>
                <CustomFormLabel>Action Status</CustomFormLabel>
                <ControlledCustomSelect
                  name="action_status"
                  fullWidth
                  value={values.action_status}
                  onChange={handleChange}
                  variant="outlined"
                  options={[
                    { label: "Pending", value: "PENDING" },
                    {
                      label: "Completed",
                      value: "COMPLETED",
                    },
                  ]}
                  displayEmpty
                  placeholder="Select One"
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <Typography variant="h5" fontWeight={800}>
                  Attachments EDOCS
                </Typography>
                <FileUpload
                  value={values.attachment}
                  // disabled
                  onChange={(file) => setFile(file)}
                  deleteDisabled={isUpdate}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <CustomFormLabel>Remarks</CustomFormLabel>
                <CustomTextField
                  multiline
                  rows={3}
                  name="remarks"
                  value={values.remarks}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.remarks}
                  helperText={errors.remarks as string}
                />
              </Grid>
              <Grid item xs={12} lg={3}>
                <CustomFormLabel>Status</CustomFormLabel>
                <ControlledCustomSelect
                  fullWidth
                  value={values.status}
                  name="status"
                  onChange={handleChange}
                  placeholder="Select one"
                  displayEmpty
                  options={[
                    { label: "Open", value: "Open" },
                    { label: "Closed", value: "Closed" },
                  ]}
                />
              </Grid>
              {/* <Grid item xs={12} lg={6}></Grid>
              {values.attachment && (
                <Grid item xs={12} lg={6}>
                  <CustomFormLabel>Attachment</CustomFormLabel>
                  <FileUpload value={values.attachment} deleteDisabled />
                </Grid>
              )} */}
            </Grid>

            <Box sx={{ mt: 4 }}>
              <RoleBasedCustomButton
                sx={{ width: "15%" }}
                disabled={saveLoading}
                variant="contained"
                moduleKey={MODULE_IDS.CSIO_EDOCS}
                type="submit"
              >
                Save
              </RoleBasedCustomButton>
            </Box>
          </form>
        </StandadCard>
      </PageLoader>
    </PageContainer>
  );
};
